import React, { useState } from 'react';
import { FiX, FiAlertCircle } from 'react-icons/fi';

/**
 * Modal de pagamento para "Outras Entidades" — input manual de código SISP, ref e montante.
 * Usado para pagar faturas pendentes de entidades descontinuadas pela SISP
 * (ex: Electra Norte 35, Electra Sul 36 — BT202411A).
 */
export interface OtherEntityModalProps {
  isOpen: boolean;
  loading?: boolean;
  onSubmit: (data: { entityCode: string; referencia: string; montante: string }) => void;
  onClose: () => void;
}

const OtherEntityModal: React.FC<OtherEntityModalProps> = ({
  isOpen,
  loading = false,
  onSubmit,
  onClose,
}) => {
  const [entityCode, setEntityCode] = useState('');
  const [referencia, setReferencia] = useState('');
  const [montante, setMontante] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});

  if (!isOpen) return null;

  const handleSubmit = () => {
    const errs: Record<string, string> = {};
    if (!entityCode.trim() || !/^\d+$/.test(entityCode.trim())) errs.entityCode = 'Código inválido';
    if (!referencia.trim() || !/^\d+$/.test(referencia.trim())) errs.referencia = 'Referência inválida';
    const amt = parseFloat(montante);
    if (!amt || amt <= 0) errs.montante = 'Valor inválido';
    if (Object.keys(errs).length) {
      setErrors(errs);
      return;
    }
    setErrors({});
    onSubmit({ entityCode: entityCode.trim(), referencia: referencia.trim(), montante });
  };

  return (
    <div
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: '#fff', borderRadius: 16, padding: 24, maxWidth: 480, width: '90%',
          boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h3 style={{ margin: 0, fontSize: 18, fontWeight: 600 }}>Outras Entidades</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}>
            <FiX size={20} />
          </button>
        </div>

        <div style={{
          background: '#E3F2FD', padding: 12, borderRadius: 8, marginBottom: 16,
          display: 'flex', gap: 10, alignItems: 'flex-start',
        }}>
          <FiAlertCircle size={18} color="#1976D2" style={{ flexShrink: 0, marginTop: 2 }} />
          <span style={{ fontSize: 12, color: '#0D47A1' }}>
            Use esta opção para pagar faturas de entidades não listadas (ex: Electra Norte 35, Electra Sul 36 — códigos descontinuados pela SISP).
          </span>
        </div>

        <div style={{ marginBottom: 14 }}>
          <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 6 }}>
            Código da Entidade
          </label>
          <input
            type="text" inputMode="numeric" maxLength={5}
            value={entityCode}
            onChange={(e) => setEntityCode(e.target.value.replace(/\D/g, ''))}
            placeholder="Ex: 35, 36, 135"
            style={inputStyle(errors.entityCode)}
          />
          {errors.entityCode && <div style={errorStyle}>{errors.entityCode}</div>}
        </div>

        <div style={{ marginBottom: 14 }}>
          <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 6 }}>
            Referência
          </label>
          <input
            type="text" inputMode="numeric"
            value={referencia}
            onChange={(e) => setReferencia(e.target.value.replace(/\D/g, ''))}
            placeholder="Número da referência da fatura"
            style={inputStyle(errors.referencia)}
          />
          {errors.referencia && <div style={errorStyle}>{errors.referencia}</div>}
        </div>

        <div style={{ marginBottom: 20 }}>
          <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 6 }}>
            Valor (CVE)
          </label>
          <input
            type="text" inputMode="numeric"
            value={montante}
            onChange={(e) => setMontante(e.target.value.replace(/[^\d.]/g, ''))}
            placeholder="0"
            style={inputStyle(errors.montante)}
          />
          {errors.montante && <div style={errorStyle}>{errors.montante}</div>}
        </div>

        <button
          onClick={handleSubmit}
          disabled={loading}
          style={{
            width: '100%', height: 48, background: '#7340BE', color: '#fff',
            border: 'none', borderRadius: 12, fontSize: 15, fontWeight: 600,
            cursor: loading ? 'not-allowed' : 'pointer', opacity: loading ? 0.6 : 1,
          }}
        >
          {loading ? 'A processar...' : 'Pagar'}
        </button>
      </div>
    </div>
  );
};

const inputStyle = (hasError?: string): React.CSSProperties => ({
  width: '100%', height: 44, padding: '0 14px',
  border: `1px solid ${hasError ? '#FF3E3E' : '#E0E0E0'}`,
  borderRadius: 10, fontSize: 14, outline: 'none', boxSizing: 'border-box',
});

const errorStyle: React.CSSProperties = { fontSize: 11, color: '#FF3E3E', marginTop: 4 };

export default OtherEntityModal;
